import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Code, CopyButton, Divider, Group, Modal, MultiSelect, NumberInput, Select, Stack, Switch, Text, TextInput } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { AlertTriangle, Check, Copy, KeyRound, Mail, Plus, RefreshCw } from 'lucide-react';
import { FEATURE_CATALOG, type PlanSummary } from '@bmai/shared';
import MegaGrid, { type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { OperatorTenantsViewModel } from './OperatorTenantsViewModel';
import type { EntitlementInput, OperatorTenant, ProvisionHandoff } from './OperatorTenantsService';

const FEATURE_GROUPS = [
    { group: 'Data & analytics (drives classification)', items: FEATURE_CATALOG.filter((f) => f.requiresIngestion).map((f) => ({ value: f.key, label: f.label })) },
    { group: 'Platform', items: FEATURE_CATALOG.filter((f) => !f.requiresIngestion).map((f) => ({ value: f.key, label: f.label })) },
];

function planLabel(p: PlanSummary): string {
    return `${p.displayName} (${p.priceMonthly === null ? 'custom' : `$${p.priceMonthly}`})`;
}

const STATUS_COLOR: Record<string, string> = { active: 'green', trial: 'blue', suspended: 'gray', cancelled: 'red' };
const HEALTH_COLOR: Record<string, string> = { ok: 'green', warn: 'yellow', critical: 'red' };

/**
 * Operator tenants list (SAAS-32/33). Each row drills into the per-tenant
 * dashboard (`/operator/tenants/:id`) where health, usage, users, and management
 * live as tabs. The list surfaces an at-a-glance health + plan + seat column
 * (merged from the account-health view).
 */
export default function OperatorTenantsPage(): React.ReactElement {
    const vm = useViewModel(OperatorTenantsViewModel);
    const navigate = useNavigate();
    const [provisionOpen, provisionCtl] = useDisclosure(false);

    useEffect(() => { void vm.load(); }, [vm]);

    const columns: MegaGridColumn<OperatorTenant>[] = [
        { key: 'name', header: 'Name', sortValue: (t) => t.name, render: (t) => <Text size="sm" fw={500}>{t.name}</Text> },
        { key: 'id', header: 'Tenant ID', sortValue: (t) => t.id, render: (t) => <Text size="sm" c="dimmed">{t.id}</Text> },
        {
            key: 'status', header: 'Status', sortValue: (t) => t.status,
            render: (t) => <Badge color={STATUS_COLOR[t.status] ?? 'gray'} variant="light" size="sm">{t.status}</Badge>,
        },
        {
            key: 'plan', header: 'Plan', sortValue: (t) => vm.healthFor(t.id)?.planName ?? '',
            render: (t) => <Text size="sm">{vm.healthFor(t.id)?.planName ?? '—'}</Text>,
        },
        {
            key: 'health', header: 'Health', sortValue: (t) => vm.healthFor(t.id)?.health ?? '',
            render: (t) => {
                const h = vm.healthFor(t.id);
                return h ? <Badge color={HEALTH_COLOR[h.health] ?? 'gray'} variant="light" size="sm">{h.health}</Badge> : <Text size="sm" c="dimmed">—</Text>;
            },
        },
        {
            key: 'seats', header: 'Seats', align: 'right', sortValue: (t) => vm.healthFor(t.id)?.seatsConsumed ?? 0,
            render: (t) => {
                const h = vm.healthFor(t.id);
                return h ? <Text size="sm">{h.seatsConsumed} / {h.seatCap ?? '∞'}</Text> : <Text size="sm" c="dimmed">—</Text>;
            },
        },
        { key: 'memberCount', header: 'Members', align: 'right', sortValue: (t) => t.memberCount, render: (t) => t.memberCount },
    ];

    return (
        <Stack gap="md">
            <PageHeaderComponent
                title="Tenants"
                subtitle="Every organization on the platform. Select a tenant to view health, usage, users, and management."
                rightToolbar={
                    <Group gap="xs">
                        <Button variant="light" leftSection={<RefreshCw size={16} />} onClick={() => void vm.load()}>Refresh</Button>
                        <Button leftSection={<Plus size={16} />} onClick={provisionCtl.open}>New tenant</Button>
                    </Group>
                }
            />

            {vm.error && <Alert color="red" icon={<AlertTriangle size={16} />}>{vm.error}</Alert>}

            <MegaGrid
                persistenceKey="operator-tenants"
                columns={columns}
                rows={vm.tenants ?? []}
                getRowKey={(t) => t.id}
                searchAccessor={(t) => `${t.id} ${t.name}`}
                searchPlaceholder="Search tenants"
                emptyMessage="No tenants"
                loading={vm.loading}
                clientPageSize={25}
                onRowClick={(t) => navigate(`/operator/tenants/${encodeURIComponent(t.id)}`)}
            />

            <ProvisionModal opened={provisionOpen} onClose={provisionCtl.close} vm={vm} />
        </Stack>
    );
}

function ProvisionModal({ opened, onClose, vm }: { opened: boolean; onClose: () => void; vm: OperatorTenantsViewModel }): React.ReactElement {
    const [tenantId, setTenantId] = useState('');
    const [tenantName, setTenantName] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [plans, setPlans] = useState<PlanSummary[]>([]);
    const [planId, setPlanId] = useState<string | null>(null);
    const [customize, setCustomize] = useState(false);
    const [features, setFeatures] = useState<string[]>([]);
    const [seatCap, setSeatCap] = useState<number | ''>('');
    const [submitting, setSubmitting] = useState(false);
    const [handoff, setHandoff] = useState<ProvisionHandoff | null>(null);

    useEffect(() => {
        if (!opened) return;
        void vm.listPlans().then((list) => {
            setPlans(list);
            const def = list.find((p) => p.name === 'free') ?? list[0] ?? null;
            if (def) applyPlanDefaults(def);
        }).catch(() => notifications.show({ color: 'red', message: 'Failed to load plans' }));
    }, [opened, vm]);

    const applyPlanDefaults = (plan: PlanSummary): void => {
        setPlanId(plan.id);
        setFeatures(plan.features);
        setSeatCap(plan.quotas.seats ?? '');
    };

    const onPlanChange = (id: string | null): void => {
        const plan = plans.find((p) => p.id === id);
        if (plan) applyPlanDefaults(plan);
        else setPlanId(id);
    };

    const reset = (): void => {
        setTenantId(''); setTenantName(''); setAdminEmail('');
        setPlanId(null); setCustomize(false); setFeatures([]); setSeatCap(''); setHandoff(null);
    };
    const close = (): void => { reset(); onClose(); };

    const submit = async (): Promise<void> => {
        setSubmitting(true);
        try {
            const selected = plans.find((p) => p.id === planId);
            const override: EntitlementInput | undefined = customize && selected
                ? { planName: selected.name, status: 'active', enabledFeatures: features, ...(seatCap === '' ? {} : { seatCap: Number(seatCap) }) }
                : undefined;
            const result = await vm.provisionWithPlan(
                { tenantId: tenantId.trim(), tenantName: tenantName.trim() || tenantId.trim(), ...(adminEmail.trim() ? { adminEmail: adminEmail.trim() } : {}) },
                { ...(planId ? { planId } : {}), ...(override ? { override } : {}) },
            );
            notifications.show({ color: 'green', message: `Provisioned ${tenantId}${selected ? ` on ${selected.displayName}` : ''}` });
            setHandoff(result ?? { emailSent: false, loginUrl: '' });
        } catch (e) {
            notifications.show({ color: 'red', title: 'Provisioning failed', message: e instanceof Error ? e.message : 'Unexpected error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal opened={opened} onClose={close} title={handoff ? 'Tenant provisioned' : 'Provision tenant'} centered>
            {handoff ? (
                <NextStepsPanel tenantId={tenantId.trim()} handoff={handoff} vm={vm} onDone={close} />
            ) : (
                <Stack gap="sm">
                    <TextInput label="Tenant ID" placeholder="acme" value={tenantId} onChange={(e) => setTenantId(e.currentTarget.value)} required />
                    <TextInput label="Name" placeholder="Acme Inc." value={tenantName} onChange={(e) => setTenantName(e.currentTarget.value)} />
                    <TextInput label="Admin email" placeholder="admin@acme.com" value={adminEmail} onChange={(e) => setAdminEmail(e.currentTarget.value)} />
                    <Select
                        label="Plan"
                        description="Seeds the tenant's features and quota buckets."
                        placeholder="Select a plan"
                        data={plans.map((p) => ({ value: p.id, label: planLabel(p) }))}
                        value={planId}
                        onChange={onPlanChange}
                        searchable
                    />
                    <Switch
                        label="Customize plan for this tenant"
                        checked={customize}
                        onChange={(e) => setCustomize(e.currentTarget.checked)}
                        disabled={!planId}
                    />
                    {customize && (
                        <>
                            <MultiSelect label="Enabled features" data={FEATURE_GROUPS} value={features} onChange={setFeatures} searchable />
                            <NumberInput label="Seat cap" description="Blank = unlimited" value={seatCap} onChange={(v) => setSeatCap(typeof v === 'number' ? v : '')} min={0} allowDecimal={false} />
                        </>
                    )}
                    <Group justify="flex-end" mt="sm">
                        <Button variant="subtle" onClick={close}>Cancel</Button>
                        <Button onClick={() => void submit()} loading={submitting} disabled={!tenantId.trim()}>Provision</Button>
                    </Group>
                </Stack>
            )}
        </Modal>
    );
}

function NextStepsPanel({ tenantId, handoff, vm, onDone }: { tenantId: string; handoff: ProvisionHandoff; vm: OperatorTenantsViewModel; onDone: () => void }): React.ReactElement {
    const [busy, setBusy] = useState<'resend' | 'temp' | null>(null);
    const [tempPassword, setTempPassword] = useState<string | null>(null);

    const resend = async (): Promise<void> => {
        setBusy('resend');
        try {
            const r = await vm.resendInvite(tenantId);
            notifications.show({ color: r.emailSent ? 'green' : 'orange', message: r.emailSent ? `Invite re-sent to ${r.adminEmail}` : 'Email could not be delivered' });
        } catch (e) {
            notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Resend failed' });
        } finally {
            setBusy(null);
        }
    };

    const genTemp = async (): Promise<void> => {
        setBusy('temp');
        try {
            const r = await vm.generateAdminTempPassword(tenantId);
            setTempPassword(r.password);
        } catch (e) {
            notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed to generate password' });
        } finally {
            setBusy(null);
        }
    };

    return (
        <Stack gap="sm">
            {handoff.emailSent
                ? <Alert color="green" variant="light" icon={<Mail size={16} />}>Invite email sent{handoff.adminEmail ? ` to ${handoff.adminEmail}` : ''}.</Alert>
                : <Alert color="orange" variant="light" icon={<AlertTriangle size={16} />}>Email delivery isn&apos;t configured — hand the admin the link or a temporary password below.</Alert>}

            {handoff.loginUrl && (
                <Stack gap={4}>
                    <Text size="xs" c="dimmed">Login URL</Text>
                    <Group gap="xs" wrap="nowrap">
                        <Code style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{handoff.loginUrl}</Code>
                        <CopyButton value={handoff.loginUrl} timeout={2000}>
                            {({ copied, copy }) => (
                                <Button size="xs" variant="light" color={copied ? 'teal' : 'blue'} leftSection={copied ? <Check size={14} /> : <Copy size={14} />} onClick={copy}>
                                    {copied ? 'Copied' : 'Copy'}
                                </Button>
                            )}
                        </CopyButton>
                    </Group>
                </Stack>
            )}

            {tempPassword && (
                <Stack gap={4}>
                    <Text size="xs" c="dimmed">Temporary password (shown once — the admin must change it at first login)</Text>
                    <Group gap="xs" wrap="nowrap">
                        <Code style={{ flex: 1 }}>{tempPassword}</Code>
                        <CopyButton value={tempPassword} timeout={2000}>
                            {({ copied, copy }) => (
                                <Button size="xs" variant="light" color={copied ? 'teal' : 'blue'} leftSection={copied ? <Check size={14} /> : <Copy size={14} />} onClick={copy}>
                                    {copied ? 'Copied' : 'Copy'}
                                </Button>
                            )}
                        </CopyButton>
                    </Group>
                </Stack>
            )}

            <Divider my={4} />
            <Group justify="space-between">
                <Group gap="xs">
                    <Button size="xs" variant="light" leftSection={<Mail size={14} />} loading={busy === 'resend'} onClick={() => void resend()}>Resend invite</Button>
                    <Button size="xs" variant="light" color="gray" leftSection={<KeyRound size={14} />} loading={busy === 'temp'} onClick={() => void genTemp()}>Temp password</Button>
                </Group>
                <Button size="xs" onClick={onDone}>Done</Button>
            </Group>
        </Stack>
    );
}
